import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { MIN_PASSWORD_LENGTH } from '@wallflower/shared';
import AuthShell, { Field, describeAuthError } from '../components/AuthShell.tsx';
import { accountApi, acceptAuth } from '../lib/accountApi.ts';
import { useDocumentTitle } from '../lib/useDocumentTitle.ts';

export default function Reset() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useDocumentTitle('Choose a new password');

  const token = params.get('token') ?? '';

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      acceptAuth(await accountApi.reset(token, password));
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthShell
        eyebrow="Nothing to reset"
        title="That link is incomplete"
        intro="Reset links carry a token. Ask for a fresh one."
      >
        <Link to="/forgot" className="btn btn-primary justify-self-start">
          Get a reset link
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow="Reset your password"
      title="Choose a new one"
      intro="Setting it signs you out everywhere else, which is the point."
    >
      <form onSubmit={onSubmit} className="grid gap-5">
        <Field
          id="reset-password"
          label="New password"
          hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={MIN_PASSWORD_LENGTH}
          required
          autoFocus
        />

        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}

        <button
          className="btn btn-primary justify-self-start"
          disabled={busy || password.length < MIN_PASSWORD_LENGTH}
        >
          {busy ? 'Saving…' : 'Set new password'}
        </button>
      </form>
    </AuthShell>
  );
}
